import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "notice", label: "Notice" },
  { key: "exited", label: "Exited" },
];

const STATUS_BADGE = {
  active: "bg-[#DCFCE7] text-[#166534]",
  inactive: "bg-paper text-quiet",
  notice: "bg-[#FEF3C7] text-[#92400E]",
  exited: "bg-[#FEE2E2] text-[#991B1B]",
};

export default function EmployeesList() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      setRows(null);
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/employees", { params });
      setRows(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load employees");
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, q]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/employees/bulk/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "employees_bulk_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download template");
    }
  };

  const onPickBulk = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("CSV exceeds 10 MB");
      ev.target.value = "";
      return;
    }
    setUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/employees/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(r.data);
      toast.success(`${r.data.imported} of ${r.data.total_rows} imported`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
      ev.target.value = "";
    }
  };

  if (err) return <div className="p-8 text-error" data-testid="employees-error">{err}</div>;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="employees-page">
      <div className="border-b border-rule pb-6 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            Team · Onboarded &amp; beyond
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Employees.</h1>
          <p className="text-sm text-quiet mt-3">
            {rows ? `${rows.length} record${rows.length === 1 ? "" : "s"}` : "Loading…"} · onboarding, active, notice &amp; exited.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
            <button
              onClick={downloadTemplate}
              data-testid="bulk-template-btn"
              className="border border-ink text-ink hover:bg-paper px-3 py-2 text-xs font-mono uppercase tracking-wider"
            >Download CSV template ↓</button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="bulk-upload-btn"
              className="bg-klein hover:bg-klein-hover text-white px-3 py-2 text-xs font-mono uppercase tracking-wider disabled:opacity-50"
            >{uploading ? "Uploading…" : "Bulk upload ↑"}</button>
            <input
              ref={fileRef}
              data-testid="bulk-upload-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onPickBulk}
            />
          </div>
          <input
            data-testid="employees-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, employee-id, email, department…"
            className="w-96 border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white"
          />
        </div>
      </div>

      {bulkResult && (
        <div className="mt-5 border border-rule bg-white p-5" data-testid="bulk-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Bulk import result</div>
              <div className="text-lg font-serif text-ink mt-1">
                <span className="text-klein" data-testid="bulk-imported">{bulkResult.imported}</span>
                {" of "}
                <span data-testid="bulk-total">{bulkResult.total_rows}</span>
                {" imported"}
                {bulkResult.skipped.length > 0 && <> · <span className="text-quiet" data-testid="bulk-skipped-count">{bulkResult.skipped.length} skipped</span></>}
                {bulkResult.errors.length > 0 && <> · <span className="text-error" data-testid="bulk-errors-count">{bulkResult.errors.length} failed</span></>}
              </div>
            </div>
            <button
              onClick={() => setBulkResult(null)}
              className="text-xs font-mono uppercase tracking-wider text-quiet hover:text-ink"
            >Dismiss ✕</button>
          </div>
          {bulkResult.skipped.length > 0 && (
            <div className="mt-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">Skipped</div>
              <table className="w-full text-sm">
                <thead className="text-left bg-paper">
                  <tr><Th>Line</Th><Th>Employee ID</Th><Th>Reason</Th></tr>
                </thead>
                <tbody>
                  {bulkResult.skipped.map((s, i) => (
                    <tr key={i} className="border-b border-rule last:border-b-0">
                      <Td><span className="font-mono text-xs">{s.line}</span></Td>
                      <Td><span className="font-mono text-xs">{s.employee_id}</span></Td>
                      <Td className="text-quiet">{s.reason}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {bulkResult.errors.length > 0 && (
            <div className="mt-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-error mb-1">Errors</div>
              <table className="w-full text-sm">
                <thead className="text-left bg-paper">
                  <tr><Th>Line</Th><Th>Employee ID</Th><Th>Error</Th></tr>
                </thead>
                <tbody>
                  {bulkResult.errors.map((er, i) => (
                    <tr key={i} className="border-b border-rule last:border-b-0">
                      <Td><span className="font-mono text-xs">{er.line}</span></Td>
                      <Td><span className="font-mono text-xs">{er.employee_id || "—"}</span></Td>
                      <Td className="text-error">{er.error}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex gap-2 border-b border-rule">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key || "all"}
            data-testid={`employees-tab-${t.key || "all"}`}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              status === t.key
                ? "border-klein text-ink"
                : "border-transparent text-quiet hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-rule overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-rule">
            <tr>
              <Th>Employee</Th>
              <Th>Emp ID</Th>
              <Th>Designation · Dept</Th>
              <Th>Reporting manager</Th>
              <Th>DOJ</Th>
              <Th align="right">Tenure</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {!rows && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-quiet text-xs">Loading…</td></tr>
            )}
            {rows && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-quiet text-xs">
                No employees match this filter. Onboard a joined hire from the <Link className="text-klein hover:underline" to="/roster">Roster</Link>.
              </td></tr>
            )}
            {rows?.map((e) => (
              <tr key={e.id} className="border-b border-rule last:border-b-0 hover:bg-paper" data-testid={`employee-row-${e.id}`}>
                <Td>
                  <div className="font-medium text-ink">{e.employee_name}</div>
                  <div className="text-xs text-quiet">{e.official_email || "—"}</div>
                </Td>
                <Td><span className="font-mono text-xs">{e.employee_id}</span></Td>
                <Td>
                  <div>{e.designation || "—"}</div>
                  <div className="text-xs text-quiet">{e.department || "—"}</div>
                </Td>
                <Td>{e.reporting_manager || "—"}</Td>
                <Td>{e.date_of_joining ? new Date(e.date_of_joining).toLocaleDateString() : "—"}</Td>
                <Td align="right">
                  {e.tenure_days != null ? (
                    <span className="font-mono text-xs">{e.tenure_days}d</span>
                  ) : "—"}
                </Td>
                <Td>
                  <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[e.employment_status] || "bg-paper text-quiet"}`}>
                    {e.employment_status}
                  </span>
                  {!e.onboarding_completed && (
                    <span className="ml-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-[#FEF3C7] text-[#92400E]">
                      pending
                    </span>
                  )}
                </Td>
                <Td>
                  {e.onboarding_completed ? (
                    <Link
                      to={`/employees/${e.id}`}
                      data-testid={`employee-view-${e.id}`}
                      className="text-xs font-mono uppercase tracking-wider text-klein hover:underline"
                    >
                      View →
                    </Link>
                  ) : (
                    <Link
                      to={`/employees/onboarding/${e.id}`}
                      data-testid={`employee-onboard-${e.id}`}
                      className="text-xs font-mono uppercase tracking-wider text-klein hover:underline"
                    >
                      Complete onboarding →
                    </Link>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-quiet text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left" }) => (
  <td className={`px-4 py-3 text-ink text-${align}`}>{children}</td>
);
